import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, AlertCircle, Terminal as TerminalIcon, RefreshCw, Link2, XCircle } from 'lucide-react';
import OpenAI from 'openai';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { openAIAssistantsService } from '../../services/openai_assistants';

interface APIConfig {
  id: string;
  nombre: string;
  api_key: string;
  organization_id: string | null;
  entorno: string;
  estado: string;
}

interface OpenAIAssistant {
  id: string;
  name: string;
  description?: string;
  model: string;
  instructions?: string;
}

interface Message {
  id?: string;
  role: 'user' | 'assistant' | 'error' | 'system';
  content: string;
  created_at?: string;
  openai_message_id?: string;
}

function Terminal() {
  const { user } = useAuthStore();
  const [apiConfigs, setApiConfigs] = useState<APIConfig[]>([]);
  const [selectedApiConfig, setSelectedApiConfig] = useState<APIConfig | null>(null);
  const [openAIAssistants, setOpenAIAssistants] = useState<OpenAIAssistant[]>([]);
  const [selectedAssistant, setSelectedAssistant] = useState<OpenAIAssistant | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(false);
  const [isConnectingAssistant, setIsConnectingAssistant] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (user) fetchApiConfigs();
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchApiConfigs = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data: configs, error } = await supabase
        .from('api_configuraciones')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setApiConfigs(configs || []);
    } catch (error: any) {
      console.error('Error fetching API configs:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedApiConfig) {
      setError('Selecciona una configuración de API primero');
      return;
    }

    try {
      setIsConnecting(true);
      setError('');
      setMessages([]);
      setOpenAIAssistants([]);
      setSelectedAssistant(null);

      // Create OpenAI client
      const client = new OpenAI({
        apiKey: selectedApiConfig.api_key.startsWith('sk-') ? selectedApiConfig.api_key : `sk-${selectedApiConfig.api_key}`,
        organization: selectedApiConfig.organization_id || undefined,
        dangerouslyAllowBrowser: true
      });

      // Test the connection by listing models
      await client.models.list();

      // Add system message about successful connection
      setMessages([{
        role: 'system',
        content: `Conectado a ${selectedApiConfig.nombre} (${selectedApiConfig.entorno})`,
        created_at: new Date().toISOString()
      }]);

      // Load assistants automatically after successful connection
      await loadAssistants();

    } catch (error: any) {
      console.error('Error connecting to OpenAI:', error);
      let errorMessage = 'Error de conexión';
      
      if (error.status === 401) {
        errorMessage = 'API key inválida o expirada';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permiso para acceder a la API';
      } else if (error.message.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet';
      } else {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setMessages([{
        role: 'error',
        content: `Error: ${errorMessage}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsConnecting(false);
    }
  };

  const loadAssistants = async () => {
    if (!selectedApiConfig) {
      setError('Selecciona una configuración de API primero');
      return;
    }

    try {
      setIsLoadingAssistants(true);
      setError('');

      // Get assistants from database
      const { data: assistants, error } = await supabase
        .from('asistentes')
        .select('*')
        .eq('estado', 'activo')
        .eq('api_configuracion_id', selectedApiConfig.id)
        .order('nombre');

      if (error) throw error;

      // Transform to OpenAI Assistant format
      const transformedAssistants = assistants.map(assistant => ({
        id: assistant.id,
        name: assistant.nombre,
        description: assistant.descripcion,
        model: assistant.modelo,
        instructions: assistant.instructions
      }));

      setOpenAIAssistants(transformedAssistants);

      // Add system message about assistants loaded
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Asistentes cargados: ${transformedAssistants.length}`,
        created_at: new Date().toISOString()
      }]);

    } catch (error: any) {
      console.error('Error loading assistants:', error);
      let errorMessage = 'Error cargando asistentes';
      
      if (error.status === 401) {
        errorMessage = 'API key inválida o expirada';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permiso para acceder a los asistentes';
      } else if (error.message.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet';
      } else {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setMessages(prev => [...prev, {
        role: 'error',
        content: `Error: ${errorMessage}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoadingAssistants(false);
    }
  };

  const handleApiConfigSelect = (configId: string) => {
    const config = apiConfigs.find(c => c.id === configId);
    if (config) {
      setSelectedApiConfig(config);
      setOpenAIAssistants([]);
      setSelectedAssistant(null);
      setMessages([]);
    }
  };

  const handleAssistantSelect = (assistantId: string) => {
    const assistant = openAIAssistants.find(a => a.id === assistantId);
    if (assistant) {
      setSelectedAssistant(assistant);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Asistente seleccionado: ${assistant.name}\nID: ${assistant.id}\nModelo: ${assistant.model}`,
        created_at: new Date().toISOString()
      }]);
    }
  };

  const handleConnectAssistant = async () => {
    if (!selectedAssistant) {
      setError('Selecciona un asistente primero');
      return;
    }

    try {
      setIsConnectingAssistant(true);
      setError('');

      // Test connection with the assistant
      const config = await openAIAssistantsService.getAssistantConfig(selectedAssistant.id);
      if (!config) throw new Error('No se pudo obtener la configuración del asistente');

      // Add success message
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Conectado al asistente: ${selectedAssistant.name}\nModelo: ${config.modelo}\nTemperatura: ${config.temperatura}\nMáx. Tokens: ${config.max_tokens}`,
        created_at: new Date().toISOString()
      }]);

      // Add welcome message from assistant
      const { response } = await openAIAssistantsService.generateResponse(
        selectedAssistant.id,
        [{
          role: 'system',
          content: 'Por favor, preséntate y describe brevemente cómo puedes ayudar.'
        }]
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response,
        created_at: new Date().toISOString()
      }]);

    } catch (error: any) {
      console.error('Error connecting to assistant:', error);
      let errorMessage = 'Error conectando al asistente';
      
      if (error.status === 401) {
        errorMessage = 'API key inválida o expirada';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permiso para acceder al asistente';
      } else if (error.message.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet';
      } else {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setMessages(prev => [...prev, {
        role: 'error',
        content: `Error: ${errorMessage}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsConnectingAssistant(false);
    }
  };

  const handleEndConversation = () => {
    // Reset conversation state
    setMessages([{
      role: 'system',
      content: 'Conversación finalizada. Selecciona un asistente para comenzar una nueva conversación.',
      created_at: new Date().toISOString()
    }]);
    setSelectedAssistant(null);
    setInputMessage('');
    setError('');

    // Focus back on assistant selection
    const assistantSelect = document.querySelector('select[name="assistant-select"]') as HTMLSelectElement;
    if (assistantSelect) {
      assistantSelect.focus();
    }
  };

  const handleSendMessage = async () => {
    if (!selectedAssistant || !inputMessage.trim()) {
      return;
    }

    try {
      setIsSending(true);
      setError('');

      // Add user message
      const userMessage: Message = {
        role: 'user',
        content: inputMessage.trim(),
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');

      // Get previous messages excluding system messages
      const chatHistory = messages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content
        }));

      // Add current message
      chatHistory.push({
        role: 'user',
        content: userMessage.content
      });

      // Generate response using OpenAI Assistant service
      const { response, openai_conversation_id } = await openAIAssistantsService.generateResponse(
        selectedAssistant.id,
        chatHistory
      );

      // Add assistant response
      const assistantMessage: Message = {
        role: 'assistant',
        content: response,
        created_at: new Date().toISOString(),
        openai_message_id: openai_conversation_id
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error: any) {
      console.error('Error sending message:', error);
      let errorMessage = 'Error al enviar el mensaje';
      
      if (error.status === 401) {
        errorMessage = 'API key inválida o expirada';
      } else if (error.status === 403) {
        errorMessage = 'No tienes permiso para acceder a la API';
      } else if (error.message.includes('network')) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet';
      } else {
        errorMessage = error.message;
      }

      setMessages(prev => [...prev, {
        role: 'error',
        content: `Error: ${errorMessage}`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsSending(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span>Cargando configuraciones...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <TerminalIcon className="w-6 h-6 text-blue-500" />
            <h1 className="text-xl font-semibold text-gray-800">Terminal</h1>
          </div>
          <div className="flex-1 mx-8 space-y-3">
            <div className="flex items-center space-x-2">
              <select
                value={selectedApiConfig?.id || ''}
                onChange={(e) => handleApiConfigSelect(e.target.value)}
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isConnecting || isLoadingAssistants}
              >
                <option value="">Seleccionar API</option>
                {apiConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.nombre} ({config.entorno})
                  </option>
                ))}
              </select>
              <button
                onClick={handleConnect}
                disabled={!selectedApiConfig || isConnecting}
                className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${isConnecting ? 'animate-spin' : ''}`} />
                <span>Conectar</span>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <select
                name="assistant-select"
                value={selectedAssistant?.id || ''}
                onChange={(e) => handleAssistantSelect(e.target.value)}
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isConnecting || isLoadingAssistants || openAIAssistants.length === 0}
              >
                <option value="">Seleccionar asistente</option>
                {openAIAssistants.map((assistant) => (
                  <option key={assistant.id} value={assistant.id}>
                    {assistant.name} ({assistant.model})
                  </option>
                ))}
              </select>
              <button
                onClick={loadAssistants}
                disabled={!selectedApiConfig || isConnecting || isLoadingAssistants}
                className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingAssistants ? 'animate-spin' : ''}`} />
                <span>Cargar</span>
              </button>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleConnectAssistant}
                disabled={!selectedAssistant || isConnectingAssistant}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Link2 className={`w-4 h-4 ${isConnectingAssistant ? 'animate-spin' : ''}`} />
                <span>Conectar Asistente</span>
              </button>
            </div>
          </div>
          <div className="w-24"></div> {/* Spacer for visual balance */}
        </div>
      </div>

      {/* Messages Window */}
      <div className="flex-1 overflow-y-auto p-6 bg-white shadow-sm border-x border-gray-200">
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`flex ${
                message.role === 'system' 
                  ? 'justify-center' 
                  : message.role === 'error'
                  ? 'justify-start'
                  : 'justify-start'
              }`}
            >
              {message.role === 'system' ? (
                <div className="bg-gray-100 px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm">
                  {message.content}
                </div>
              ) : (
                <div
                  className={`flex items-start space-x-2 max-w-[80%] ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                      message.role === 'user'
                        ? 'bg-blue-500'
                        : message.role === 'error'
                        ? 'bg-red-500'
                        : 'bg-gray-200'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : message.role === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-gray-700" />
                    )}
                  </div>
                  <div
                    className={`p-4 rounded-lg font-mono text-sm whitespace-pre-wrap shadow-sm ${
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : message.role === 'error'
                        ? 'bg-red-50 text-red-700 border border-red-100'
                        : 'bg-gray-50 text-gray-900 border border-gray-100'
                    }`}
                  >
                    {message.content}
                  </div>
                </div>
              )}
            </div>
          ))}
          {messages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay mensajes aún</p>
              <p className="text-sm">Selecciona una API y un asistente para comenzar</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white shadow-sm border-t border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start space-x-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none shadow-sm"
              rows={3}
              disabled={!selectedAssistant || isConnecting || isLoadingAssistants || isSending}
            />
            <div className="flex flex-col space-y-2">
              <button
                onClick={handleSendMessage}
                disabled={!selectedAssistant || !inputMessage.trim() || isConnecting || isLoadingAssistants || isSending}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Enviar</span>
                  </>
                )}
              </button>
              <button
                onClick={handleEndConversation}
                disabled={!selectedAssistant || isConnecting || isLoadingAssistants || isSending}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
              >
                <XCircle className="w-5 h-5" />
                <span>Finalizar</span>
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Terminal;